import { decodeEntities } from "../lib/format";
import { apiFetch } from "./client";
import type {
  ApprovalDocument,
  ApprovalPage,
  ApprovalStatus,
  ApprovalSummary,
  AssetTranscription,
  ExtractionRun,
  MoodleCourse,
  MoodleCourseDetail,
  MoodleDocument,
  MoodleAsset,
  MoodleDocumentDetail,
  MoodleModuleListItem,
  SyncEvent,
  SyncPreview,
  SyncRun,
  TranscriptionStatus,
  TranscriptionFailureGroup,
  TranscriptionWatcher,
  TriggerResult,
} from "./types";

// All /moodle/* routes require admin, super_admin or root
// (app/api/v1/moodle.py). Triggers are called WITHOUT ?inline=true so they
// queue to Celery; the UI tracks each job by polling GET /tasks/{task_id}.

type QueryValue = string | number | boolean | null | undefined;

// Array values repeat the key (?status=approved&status=rejected).
export function query(params: Record<string, QueryValue | QueryValue[]>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    for (const v of Array.isArray(value) ? value : [value]) {
      if (v !== undefined && v !== null && v !== "") search.append(key, String(v));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

// --- courses ---------------------------------------------------------------

function decodeCourse<C extends MoodleCourse>(course: C): C {
  const decoded = { ...course, fullname: decodeEntities(course.fullname), shortname: decodeEntities(course.shortname) };
  if ("modules" in decoded) {
    const detail = decoded as unknown as MoodleCourseDetail;
    detail.sections = detail.sections.map((s) => ({ ...s, name: decodeEntities(s.name) }));
    detail.modules = detail.modules.map((m) => ({ ...m, name: decodeEntities(m.name) }));
  }
  return decoded;
}

export async function listCourses(opts: { limit?: number; offset?: number; includeDeleted?: boolean } = {}) {
  const res = await apiFetch<{ courses: MoodleCourse[]; total: number; limit: number; offset: number }>(
    `/moodle/courses${query({
      limit: opts.limit ?? 200,
      offset: opts.offset,
      include_deleted: opts.includeDeleted || undefined,
    })}`
  );
  return { ...res, courses: res.courses.map(decodeCourse) };
}

export async function getCourse(moodleId: number) {
  const { course } = await apiFetch<{ course: MoodleCourseDetail }>(`/moodle/courses/${moodleId}`);
  return decodeCourse(course);
}

export async function syncCourse(moodleId: number) {
  const { course } = await apiFetch<{ course: MoodleCourseDetail }>(
    `/moodle/courses/${moodleId}/sync`,
    { method: "POST" }
  );
  return decodeCourse(course);
}

// --- sync --------------------------------------------------------------------

// Dry run: what a sync would create/update/retire. Always inline and saves
// nothing, but makes the same Moodle calls as a real sync, so it's slow.
// `removed` courses only appear in a full preview (no ids).
export async function previewSync(courseMoodleIds?: number[]) {
  const res = await apiFetch<SyncPreview>("/moodle/sync/preview", {
    method: "POST",
    body: courseMoodleIds?.length ? { course_moodle_ids: courseMoodleIds } : undefined,
  });
  return {
    ...res,
    courses: res.courses.map((c) => ({ ...c, fullname: decodeEntities(c.fullname), shortname: decodeEntities(c.shortname) })),
  };
}

// No ids = full sync (can retire courses Moodle no longer has). With ids, only
// those courses are synced and nothing else is retired. Inline responses also
// carry the synced courses and `missing`; the UI only needs the run.
export function syncAll(courseMoodleIds?: number[]) {
  return apiFetch<TriggerResult<SyncRun>>("/moodle/sync", {
    method: "POST",
    body: courseMoodleIds?.length ? { course_moodle_ids: courseMoodleIds } : undefined,
  });
}

export async function listSyncRuns(limit = 20) {
  const { runs } = await apiFetch<{ runs: SyncRun[] }>(`/moodle/sync/runs${query({ limit })}`);
  return runs;
}

export function listChanges(opts: {
  sinceId?: number;
  limit?: number;
  syncRunId?: number;
  action?: "created" | "updated" | "deleted";
  courseMoodleId?: number;
  entityType?: string;
} = {}) {
  return apiFetch<{ events: SyncEvent[]; next_since_id: number; count: number }>(
    `/moodle/changes${query({
      sync_run_id: opts.syncRunId,
      action: opts.action,
      since_id: opts.sinceId,
      limit: opts.limit ?? 500,
      course_moodle_id: opts.courseMoodleId,
      entity_type: opts.entityType,
    })}`
  );
}

// --- extraction --------------------------------------------------------------

export function extractAll() {
  return apiFetch<TriggerResult<ExtractionRun>>("/moodle/extract", { method: "POST" });
}

// Retry modules whose last extraction failed: the chosen cmids and/or courses,
// or (nothing passed) every failing module. Queued unless no worker is up.
export function retryFailedExtractions(scope: { cmids?: number[]; courseMoodleIds?: number[] } = {}) {
  return apiFetch<TriggerResult<ExtractionRun>>("/moodle/extract/retry-failed", {
    method: "POST",
    body: {
      cmids: scope.cmids?.length ? scope.cmids : null,
      course_moodle_ids: scope.courseMoodleIds?.length ? scope.courseMoodleIds : null,
    },
  });
}

export async function extractModule(cmid: number) {
  const { run } = await apiFetch<{ run: ExtractionRun }>(`/moodle/modules/${cmid}/extract`, {
    method: "POST",
  });
  return run;
}

export async function listExtractionRuns(limit = 20) {
  const res = await apiFetch<{ runs: ExtractionRun[]; pending_modules: number }>(
    `/moodle/extract/runs${query({ limit })}`
  );
  // failure_details is [] on runs from before the API recorded it.
  return { ...res, runs: res.runs.map((r) => ({ ...r, failure_details: r.failure_details ?? [] })) };
}

// Mirrored modules across courses with their extraction state. failed=true is
// the "failed extractions" view; retry one with extractModule(cmid).
export async function listModules(opts: {
  courseMoodleId?: number;
  failed?: boolean;
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
} = {}) {
  const res = await apiFetch<{ modules: MoodleModuleListItem[]; total: number; limit: number; offset: number }>(
    `/moodle/modules${query({
      course_moodle_id: opts.courseMoodleId,
      failed: opts.failed,
      include_deleted: opts.includeDeleted || undefined,
      limit: opts.limit ?? 100,
      offset: opts.offset,
    })}`
  );
  return {
    ...res,
    modules: res.modules.map((m) => ({
      ...m,
      name: decodeEntities(m.name),
      course_fullname: decodeEntities(m.course_fullname),
      section_name: decodeEntities(m.section_name),
    })),
  };
}

// --- media transcription ------------------------------------------------------

// Audio/video assets, optionally in one transcription state.
export async function listAssets(opts: {
  status?: TranscriptionStatus;
  courseMoodleId?: number;
  offset?: number;
  limit?: number;
} = {}) {
  const res = await apiFetch<{ assets: MoodleAsset[]; total: number; limit: number; offset: number }>(
    `/moodle/assets${query({
      media: true,
      transcription_status: opts.status,
      course_moodle_id: opts.courseMoodleId,
      offset: opts.offset,
      limit: opts.limit ?? 50,
    })}`
  );
  return {
    ...res,
    assets: res.assets.map((a) => ({
      ...a,
      course_fullname: decodeEntities(a.course_fullname),
      module_name: decodeEntities(a.module_name),
    })),
  };
}

// 404 when the asset has no transcription yet.
export function getAssetTranscription(assetId: number) {
  return apiFetch<AssetTranscription>(`/moodle/assets/${assetId}/transcription`);
}

// Queued (never inline): either chosen files, or every pending/failed one.
export function triggerTranscriptions(scope: { assetIds?: number[]; only?: "pending" | "failed" }) {
  return apiFetch<{ mode: "queued"; task_id: string } | { mode: "inline"; assets_seen: number; completed: number; failed: number }>(
    "/moodle/transcriptions",
    { method: "POST", body: { asset_ids: scope.assetIds, only: scope.only } }
  );
}

export function getTranscriptionFailures() {
  return apiFetch<{ groups: TranscriptionFailureGroup[]; total_files: number }>("/moodle/transcriptions/failures");
}

export function getTranscriptionWatcher() {
  return apiFetch<TranscriptionWatcher>("/moodle/transcriptions/watcher");
}

export function setTranscriptionWatcher(paused: boolean) {
  return apiFetch<TranscriptionWatcher>("/moodle/transcriptions/watcher", {
    method: "PUT",
    body: { paused },
  });
}

// --- documents & approval ------------------------------------------------------

function decodeTitle<D extends { title: string | null }>(doc: D): D {
  return { ...doc, title: decodeEntities(doc.title) };
}

export async function listDocuments(opts: { sinceId?: number; limit?: number; courseMoodleId?: number } = {}) {
  const res = await apiFetch<{ documents: MoodleDocument[]; next_since_id: number; count: number }>(
    `/moodle/documents${query({
      since_id: opts.sinceId,
      limit: opts.limit ?? 200,
      course_moodle_id: opts.courseMoodleId,
    })}`
  );
  return { ...res, documents: res.documents.map(decodeTitle) };
}

export async function getDocument(documentId: number) {
  const { document } = await apiFetch<{ document: MoodleDocumentDetail }>(
    `/moodle/documents/${documentId}`
  );
  return decodeTitle(document);
}

function decodeApproval(doc: ApprovalDocument): ApprovalDocument {
  return {
    ...doc,
    title: decodeEntities(doc.title),
    course_fullname: decodeEntities(doc.course_fullname),
    module_name: decodeEntities(doc.module_name),
    section_name: decodeEntities(doc.section_name),
  };
}

function decodePage(page: ApprovalPage): ApprovalPage {
  return { ...page, documents: page.documents.map(decodeApproval) };
}

// `count` is this page's size; `total` is the whole matching queue.
export async function listPendingApprovals(opts: { courseMoodleId?: number; moduleCmid?: number; limit?: number; offset?: number } = {}) {
  const res = await apiFetch<ApprovalPage>(
    `/moodle/approval/pending${query({
      course_moodle_id: opts.courseMoodleId,
      module_cmid: opts.moduleCmid,
      limit: opts.limit ?? 50,
      offset: opts.offset,
    })}`
  );
  return decodePage(res);
}

// Review history, newest decision first. The API defaults to approved + rejected.
export async function listReviewedDocuments(opts: {
  statuses?: ApprovalStatus[];
  courseMoodleId?: number;
  moduleCmid?: number;
  reviewedByUserId?: number;
  limit?: number;
  offset?: number;
} = {}) {
  const res = await apiFetch<ApprovalPage>(
    `/moodle/approval/documents${query({
      status: opts.statuses,
      course_moodle_id: opts.courseMoodleId,
      module_cmid: opts.moduleCmid,
      reviewed_by_user_id: opts.reviewedByUserId,
      limit: opts.limit ?? 50,
      offset: opts.offset,
    })}`
  );
  return decodePage(res);
}

export async function getApprovalSummary() {
  const res = await apiFetch<ApprovalSummary>("/moodle/approval/summary");
  return {
    ...res,
    courses: res.courses.map((c) => ({ ...c, course_fullname: decodeEntities(c.course_fullname) })),
  };
}

export async function approveDocument(documentId: number, note?: string) {
  return decodeApproval(await apiFetch<ApprovalDocument>(`/moodle/documents/${documentId}/approve`, {
    method: "POST",
    body: { note: note || null },
  }));
}

export async function rejectDocument(documentId: number, note?: string) {
  return decodeApproval(await apiFetch<ApprovalDocument>(`/moodle/documents/${documentId}/reject`, {
    method: "POST",
    body: { note: note || null },
  }));
}

// Undo a decision: approved/rejected → pending (and out of the search index).
// 409 if the document is already pending or retired.
export async function reopenDocument(documentId: number, note?: string) {
  return decodeApproval(await apiFetch<ApprovalDocument>(`/moodle/documents/${documentId}/reopen`, {
    method: "POST",
    body: { note: note || null },
  }));
}

export interface BulkScope {
  documentIds?: number[];
  courseMoodleId?: number;
  // Several whole courses at once (the course-level review queue).
  courseMoodleIds?: number[];
  moduleCmid?: number;
  note?: string;
}

// Select by explicit ids, by course and/or module, or (nothing passed) the
// whole pending queue. Only pending documents are affected.
function bulkBody(scope: BulkScope) {
  return {
    document_ids: scope.documentIds?.length ? scope.documentIds : null,
    course_moodle_id: scope.courseMoodleId ?? null,
    course_moodle_ids: scope.courseMoodleIds?.length ? scope.courseMoodleIds : null,
    module_cmid: scope.moduleCmid ?? null,
    note: scope.note || null,
  };
}

export function bulkApprove(scope: BulkScope) {
  return apiFetch<{ documents: ApprovalDocument[]; count: number }>("/moodle/approval/bulk-approve", {
    method: "POST",
    body: bulkBody(scope),
  });
}

export function bulkReject(scope: BulkScope) {
  return apiFetch<{ documents: ApprovalDocument[]; count: number }>("/moodle/approval/bulk-reject", {
    method: "POST",
    body: bulkBody(scope),
  });
}

// --- "latest run" helpers (from the dashboard-overview branch) ------------------
// Kept so code written against that branch's API keeps working. Shapes
// verified against the live API on 2026-09-23 (root dev account).

export type { SyncRun } from "./types";
export type ExtractRun = ExtractionRun;
export type ExtractRunsResponse = Awaited<ReturnType<typeof listExtractionRuns>>;
export type ApprovalPending = Awaited<ReturnType<typeof listPendingApprovals>>;

export async function getLatestSyncRun() {
  return { runs: await listSyncRuns(1) };
}

export function getLatestExtractRun() {
  return listExtractionRuns(1);
}

// `count` is the page size; `total` is the whole queue.
export function getPendingApprovals(courseId?: number) {
  return listPendingApprovals({ courseMoodleId: courseId });
}
